import { type Observable } from 'rxjs';

import { inject, Service } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';

import { type ApiResponse, extractMappedDataNonNull } from '@entities/http';

import { type LoginResponseDto } from './dto/login.dto';
import { type ForgotPasswordDto } from './dto/forgot-password.dto';
import { type ResetPasswordDto } from './dto/reset-password-payload.dto';

import { type AuthToken } from './models/token';
import { type LoginPayload } from './models/login';
import { loginFromDto } from './mappers/login.mapper';
import { accessTokenFromForgotPasswordDto } from './mappers/forgot-password';

import { ALLOW_REFRESH } from '@core/http';

@Service()
export class AuthApi {
  private readonly http = inject(HttpClient);

  login(payload: LoginPayload): Observable<AuthToken> {
    return this.http
      .post<ApiResponse<LoginResponseDto>>('/admin/auth/login', payload, {
        context: new HttpContext().set(ALLOW_REFRESH, false),
      })
      .pipe(extractMappedDataNonNull(loginFromDto));
  }

  refreshToken(): Observable<AuthToken> {
    return this.http
      .post<ApiResponse<LoginResponseDto>>('/admin/auth/refresh', null, {
        context: new HttpContext().set(ALLOW_REFRESH, false),
      })
      .pipe(extractMappedDataNonNull(loginFromDto));
  }

  logout(): Observable<void> {
    return this.http.post<void>('/admin/auth/logout', null, {
      context: new HttpContext().set(ALLOW_REFRESH, false),
    });
  }

  forgotPassword(email: string): Observable<AuthToken> {
    return this.http
      .post<ApiResponse<ForgotPasswordDto>>(
        '/admin/auth/forgot-password',
        { email },
        {
          context: new HttpContext().set(ALLOW_REFRESH, false),
        },
      )
      .pipe(extractMappedDataNonNull(accessTokenFromForgotPasswordDto));
  }

  resetPassword(data: ResetPasswordDto): Observable<null> {
    return this.http.post<null>('/admin/auth/reset-password', data, {
      context: new HttpContext().set(ALLOW_REFRESH, false),
    });
  }
}
